// ---------------------------------------------------------------------------
// The write path. Every mutation in the app goes through this module -- no
// component talks to Supabase directly (PLAN 5.2).
//
// The contract has not changed since phase 2, which is the point: only the body
// of recordVisit did.
//
//   1. Canvasser submits the form.
//   2. The client generates a UUID.
//   3. The record is written to IndexedDB with synced: false.
//   4. The UI updates optimistically and immediately -- recordVisit returns
//      synchronously and never awaits anything.
//   5. The sync loop picks it up.
// ---------------------------------------------------------------------------

import {
  houseNoteDraftSchema,
  personContactDraftSchema,
  visitDraftSchema,
  type HouseNoteDraftInput,
  type HouseholdNote,
  type PersonContact,
  type PersonContactDraftInput,
  type Visit,
  type VisitDraftInput,
} from '@yucanvas/shared';
import {
  db,
  type LocalHousehold,
  type LocalHouseNote,
  type LocalPerson,
  type LocalPersonContact,
  type LocalVisit,
} from '../offline/db';
import { uuidv4 } from '../lib/uuid';
import { addressKey } from '../offline/address-key';
import { notifyQueued, onSyncState } from '../offline/sync';
import { supabase } from '../lib/supabase';

export interface WriteError {
  visitId: string;
  message: string;
}

type VisitListener = (visit: Visit) => void;
type ErrorListener = (error: WriteError) => void;

const visitListeners = new Set<VisitListener>();
const errorListeners = new Set<ErrorListener>();

/** Fires the moment a visit is accepted locally, before the server sees it. */
export function onVisitRecorded(fn: VisitListener): () => void {
  visitListeners.add(fn);
  return () => visitListeners.delete(fn);
}

export function onWriteError(fn: ErrorListener): () => void {
  errorListeners.add(fn);
  return () => errorListeners.delete(fn);
}

/**
 * "8 visits waiting to upload", turning green at zero (PLAN 5.7). Backed by the
 * IndexedDB queue, so it survives a force-quit and still reads 8 on reopen.
 */
export function onPendingChange(fn: (count: number) => void): () => void {
  return onSyncState((s) => fn(s.pending));
}

export function newContactId(): string {
  return uuidv4();
}

export function newVisitId(): string {
  return uuidv4();
}

/**
 * Records a visit. Throws only on validation failure. Nothing here awaits the
 * network or even the disk: the form closes on the next frame whether the
 * canvasser has signal or not (PLAN 5.2, 13.9).
 */
export function recordVisit(input: VisitDraftInput): Visit {
  const draft = visitDraftSchema.parse(input);

  const local: LocalVisit = {
    ...draft,
    // The server stamps its own on arrival; the pull replaces this (PLAN 5.3).
    received_at: null,
    synced: 0,
    attempts: 0,
    last_error: null,
  };

  for (const fn of visitListeners) fn(local as Visit);

  void db.visits
    .put(local)
    .then(() => notifyQueued())
    .catch((cause: unknown) => {
      for (const fn of errorListeners) {
        fn({
          visitId: draft.id,
          message:
            cause instanceof Error
              ? `Could not save to this device: ${cause.message}`
              : 'Could not save to this device.',
        });
      }
    });

  return local as Visit;
}

/**
 * Canvassers may flag a household but must not edit anything else about it, so
 * this goes through the RPC rather than an UPDATE (PLAN 4.5).
 *
 * Unlike visits this is not queued offline. It is shared mutable state rather
 * than an append-only observation, and a queue of stale flags applied later
 * would be worse than an honest failure now. The caller surfaces the error.
 */
export async function flagDoNotContact(
  householdId: string,
  reason: string | null,
  canvasserId: string,
): Promise<void> {
  if (!navigator.onLine) {
    throw new Error('Flagging do-not-contact needs a connection. Try again in signal.');
  }
  const { error } = await supabase.rpc('set_do_not_contact', {
    p_household_id: householdId,
    p_reason: reason ?? '',
  });
  if (error) throw error;

  // Reflect it locally so the address is suppressed straight away. `at` and
  // `by` are what unflagDoNotContact's undo window is measured against, and
  // are approximated here rather than read back: the server stamps its own
  // now(), and being a network round-trip early only shortens this device's
  // offer of the button.
  await db.households.update(householdId, {
    do_not_contact: true,
    do_not_contact_reason: reason,
    do_not_contact_at: new Date().toISOString(),
    do_not_contact_by: canvasserId,
  });
}

/**
 * Takes a flag back off an address.
 *
 * The server decides who may: organizers always, and the canvasser who set it
 * for DO_NOT_CONTACT_UNDO_MINUTES afterwards
 * (supabase/migrations/20260908001100_undo_do_not_contact.sql). The caller
 * should already have asked canUndoDoNotContact() before drawing a button, so
 * a rejection here is a stale flag or a drifting clock rather than the normal
 * path -- either way the RPC's message is the one worth showing.
 *
 * Not queued offline, for the same reason flagging is not: this is shared
 * mutable state, and an unflag replayed an hour later would put an address
 * somebody asked to be left alone back on every walk list.
 */
export async function unflagDoNotContact(householdId: string): Promise<void> {
  if (!navigator.onLine) {
    throw new Error('Undoing a do-not-contact flag needs a connection. Try again in signal.');
  }
  const { error } = await supabase.rpc('clear_do_not_contact', {
    p_household_id: householdId,
  });
  if (error) throw error;

  await db.households.update(householdId, {
    do_not_contact: false,
    do_not_contact_reason: null,
    do_not_contact_at: null,
    do_not_contact_by: null,
  });
}

/**
 * Records one person at the door, independently of their housemates
 * (docs/campaign-requirements.md § 3.1).
 *
 * Same contract as recordVisit: validates and returns synchronously, never
 * awaits the network, id generated here so a replayed push is a no-op.
 */
export function recordPersonContact(
  input: PersonContactDraftInput,
): PersonContact {
  const draft = personContactDraftSchema.parse(input);

  const local: LocalPersonContact = {
    ...draft,
    received_at: null,
    synced: 0,
    attempts: 0,
    last_error: null,
  };

  void db.personContacts
    .put(local)
    .then(() => notifyQueued())
    .catch((cause: unknown) => {
      for (const fn of errorListeners) {
        fn({
          visitId: draft.visit_id,
          message:
            cause instanceof Error
              ? `Could not save to this device: ${cause.message}`
              : 'Could not save to this device.',
        });
      }
    });

  return local as PersonContact;
}

/**
 * Records a note about an address.
 *
 * Append-only, so two canvassers writing about the same house at the same time
 * produce two notes rather than one erasing the other
 * (docs/campaign-requirements.md § 3.6). Same contract as recordVisit:
 * validates and returns synchronously, never awaits the network.
 */
export function recordHouseNote(
  input: HouseNoteDraftInput,
  authorName: string | null = null,
): HouseholdNote {
  const draft = houseNoteDraftSchema.parse(input);

  const local: LocalHouseNote = {
    ...draft,
    received_at: null,
    author_name: authorName,
    synced: 0,
    attempts: 0,
    last_error: null,
  };

  void db.houseNotes
    .put(local)
    .then(() => notifyQueued())
    .catch((cause: unknown) => {
      for (const fn of errorListeners) {
        fn({
          visitId: draft.id,
          message:
            cause instanceof Error
              ? `Could not save to this device: ${cause.message}`
              : 'Could not save to this device.',
        });
      }
    });

  return local as HouseholdNote;
}

/**
 * Adds a home a canvasser is standing in front of that the address data
 * missed (docs/campaign-requirements.md § 3.10).
 *
 * Written to the device first and queued, like a visit: this happens at a
 * door, which is where there is least likely to be signal. The id is generated
 * here so the visit recorded seconds later can reference it before either has
 * reached the server.
 */
export interface AddressClash {
  kind: 'active' | 'removed';
  household: LocalHousehold;
}

/**
 * An address already on this device, active or removed.
 *
 * Checked before creating anything, because the database will refuse a second
 * row with the same address and the canvasser would only find out on the next
 * sync -- by which time they have walked on.
 */
export async function findAddressClash(
  turfId: string,
  street: string,
  unit: string | null,
  postal: string | null,
): Promise<AddressClash | null> {
  const key = addressKey(street, unit, postal);
  const inTurf = await db.households.where('turf_id').equals(turfId).toArray();

  const match = inTurf.find(
    (h) => addressKey(h.street_address, h.unit, h.postal_code) === key,
  );
  if (!match) return null;

  return { kind: match.active ? 'active' : 'removed', household: match };
}

/**
 * Brings back a home that was removed, moving it to where the canvasser is
 * standing now.
 *
 * Reviving rather than inserting a second row: the address is unique in the
 * database, and the knocks already logged there belong to this home.
 */
export async function reviveHousehold(
  householdId: string,
  at: { lat: number; lon: number },
): Promise<void> {
  await db.households.update(householdId, {
    active: true,
    lat: at.lat,
    lon: at.lon,
    // Cancels a removal that has not reached the server yet; queues a revival
    // if it has.
    pending_removal: undefined,
    pending_revival: 0,
    attempts: 0,
    last_error: null,
  });
  notifyQueued();
}

export function recordNewHousehold(input: {
  turfId: string;
  lat: number;
  lon: number;
  streetAddress: string;
  unit: string | null;
  city: string;
  postalCode: string | null;
}): LocalHousehold {
  const streetAddress = input.streetAddress.trim();
  if (streetAddress === '') throw new Error('Give the home an address.');

  const now = new Date().toISOString();
  const household: LocalHousehold = {
    id: uuidv4(),
    street_address: streetAddress,
    unit: input.unit?.trim() || null,
    city: input.city,
    state: 'CA',
    postal_code: input.postalCode,
    lat: input.lat,
    lon: input.lon,
    source: 'manual',
    source_id: null,
    turf_id: input.turfId,
    do_not_contact: false,
    do_not_contact_reason: null,
    do_not_contact_at: null,
    do_not_contact_by: null,
    active: true,
    created_at: now,
    updated_at: now,
    synced: 0,
    attempts: 0,
    last_error: null,
  };

  void db.households.put(household).then(() => notifyQueued());
  return household;
}

/** A resident recorded at the door who is not in the voter file. */
export function recordNewPerson(householdId: string, name: string): LocalPerson {
  const trimmed = name.trim();
  if (trimmed === '') throw new Error('Enter a name.');

  // Split on the last space: "Maria Delgado" and "Maria" both work, and
  // "Maria del Carmen Delgado" keeps its surname whole.
  const cut = trimmed.lastIndexOf(' ');
  const first = cut === -1 ? trimmed : trimmed.slice(0, cut);
  const last = cut === -1 ? '' : trimmed.slice(cut + 1);

  const id = uuidv4();
  const now = new Date().toISOString();
  const person: LocalPerson = {
    id,
    household_id: householdId,
    // The server synthesises 'manual:<id>'; mirrored here so the local row
    // matches the one that comes back on the next pull.
    external_id: `manual:${id}`,
    first_name: first,
    middle_name: null,
    last_name: last,
    suffix: null,
    party: null,
    gender: null,
    age: null,
    age_as_of: null,
    registration_status: null,
    vbm_status: null,
    precinct: null,
    tier: null,
    turnout_score: null,
    cell_phone: null,
    landline: null,
    do_not_call: false,
    callable: false,
    mailable: false,
    deceased: false,
    moved: false,
    active: true,
    created_at: now,
    updated_at: now,
    synced: 0,
    attempts: 0,
    last_error: null,
  };

  void db.people.put(person).then(() => notifyQueued());
  return person;
}

/**
 * Takes back a home a canvasser added.
 *
 * Two paths, because a home that never reached the server is a different thing
 * from one that did:
 *
 *   * Never pushed -- delete it outright, along with the resident and the
 *     knock queued against it. Nothing existed anywhere else, so nothing needs
 *     telling, and pushing a home only to deactivate it a second later would
 *     be silly.
 *   * Already on the server -- mark it inactive locally so it leaves the map
 *     now, and queue the removal. Never a hard delete: visits cascade, and a
 *     deleted row cannot travel through a delta pull, so other phones would
 *     keep showing it (PLAN 5.6).
 */
export async function removeHousehold(householdId: string): Promise<void> {
  const household = await db.households.get(householdId);
  if (!household) return;

  if (household.source !== 'manual') {
    throw new Error('Only a home added by a canvasser can be removed.');
  }

  if (household.synced === 0) {
    const people = await db.people.where('household_id').equals(householdId).toArray();
    const personIds = people.map((p) => p.id);

    await db.transaction(
      'rw',
      [db.households, db.people, db.visits, db.personContacts, db.houseNotes],
      async () => {
        const contacts = await db.personContacts
          .where('person_id')
          .anyOf(personIds)
          .toArray();
        await db.personContacts.bulkDelete(contacts.map((c) => c.id));

        const visits = await db.visits
          .where('household_id')
          .equals(householdId)
          .toArray();
        await db.visits.bulkDelete(visits.map((v) => v.id));

        const notes = await db.houseNotes
          .where('household_id')
          .equals(householdId)
          .toArray();
        await db.houseNotes.bulkDelete(notes.map((n) => n.id));

        await db.people.bulkDelete(personIds);
        await db.households.delete(householdId);
      },
    );

    notifyQueued();
    return;
  }

  // Hidden immediately; the server is told on the next sync.
  await db.households.update(householdId, {
    active: false,
    pending_removal: 0,
    attempts: 0,
    last_error: null,
  });
  notifyQueued();
}
